import { useState, useRef, useEffect } from "react";
import { Mic, Square } from "lucide-react";
import { createVAD, type VADProcessor } from "@fluentry/core";
import { Button } from "~/components/ui/button";
import { Progress } from "~/components/ui/progress";
import { Badge } from "~/components/ui/badge";
import { ErrorBanner } from "~/components/ui/error-banner";

interface VoiceInputProps {
  onSpeechEnd?: (audioData: Float32Array) => void;
  onVolumeChange?: (volume: number) => void;
  createVAD?: typeof createVAD;
}

export function VoiceInput({
  onSpeechEnd,
  onVolumeChange,
  createVAD: createVADFactory,
}: VoiceInputProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [volume, setVolume] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const vadRef = useRef<VADProcessor | null>(null);
  const createVADFn = createVADFactory ?? createVAD;

  useEffect(() => {
    return () => {
      if (vadRef.current) {
        vadRef.current.stop();
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      setError(null);

      vadRef.current = createVADFn(
        {
          threshold: 0.02,
          silenceTimeout: 1000,
          minSpeechDuration: 250,
          sampleRate: 16000,
        },
        {
          onSpeechStart: () => {
            console.log("Speech started");
          },
          onSpeechEnd: (audioData: Float32Array) => {
            console.log("Speech ended, audio length:", audioData.length);
            onSpeechEnd?.(audioData);
          },
          onVolumeChange: (vol: number) => {
            setVolume(vol);
            onVolumeChange?.(vol);
          },
        },
      );

      await vadRef.current.start();
      setIsRecording(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start recording");
      console.error("Recording error:", err);
    }
  };

  const stopRecording = () => {
    if (vadRef.current) {
      vadRef.current.stop();
      vadRef.current = null;
    }
    setIsRecording(false);
    setVolume(0);
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    void startRecording();
  };

  const meterValue = Math.min(100, volume * 500);

  return (
    <div className="space-y-3">
      <Button
        type="button"
        onClick={toggleRecording}
        variant={isRecording ? "destructive" : "default"}
        className="h-11 px-4"
      >
        {isRecording ? (
          <>
            <Square className="mr-2 h-4 w-4" />
            Stop Recording
          </>
        ) : (
          <>
            <Mic className="mr-2 h-4 w-4" />
            Start Voice Input
          </>
        )}
      </Button>

      <div className="flex items-center gap-2">
        {isRecording ? (
          <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Listening...</Badge>
        ) : (
          <Badge variant="secondary">Idle</Badge>
        )}
      </div>

      {isRecording ? <Progress value={meterValue} aria-label="Voice volume" /> : null}

      {error ? <ErrorBanner title="Voice Input Error" message={error} /> : null}
    </div>
  );
}
